import { db, auth } from '../config/firebase';
import {
  collection,
  doc,
  onSnapshot,
  runTransaction,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { parseCuitDigits } from '../utils/formatters';
import { isValidCuit } from '../utils/cuit';

// Catálogo de colocadores de licitaciones. El id de cada documento es el CUIT (11
// dígitos, sin guiones): así no puede haber dos colocadores con el mismo CUIT y las
// reglas lo verifican (`cuit == id`). Nunca se borra: se desactiva con active:false.
const COLECCION = 'colocadores';

export const COLOCADOR_DUPLICADO = 'colocador-duplicado';

const errorConCodigo = (code, message) => Object.assign(new Error(message), { code });

const ordenarPorNombre = (a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' });

/**
 * Suscribe a todo el catálogo (activos e inactivos), ordenado por nombre.
 * @returns {() => void} función de desuscripción
 */
export const subscribeColocadores = (onData, onError) => onSnapshot(
  collection(db, COLECCION),
  (snapshot) => onData(snapshot.docs.map(d => ({ id: d.id, ...d.data() })).sort(ordenarPorNombre)),
  (error) => {
    console.error('colocadoresService.subscribeColocadores failed:', error);
    if (onError) onError(error);
  },
);

/**
 * Da de alta un colocador. Falla con `code: 'colocador-duplicado'` si ya existe uno con
 * ese CUIT (activo o no): en ese caso se reactiva, no se vuelve a crear.
 */
export const createColocador = async ({ nombre, cuit }) => {
  const cuitDigits = parseCuitDigits(cuit);
  const nombreLimpio = String(nombre || '').trim();
  if (!nombreLimpio) throw errorConCodigo('invalid-argument', 'El nombre es obligatorio.');
  if (!isValidCuit(cuitDigits)) throw errorConCodigo('invalid-argument', 'El CUIT no es válido.');
  const ref = doc(db, COLECCION, cuitDigits);
  try {
    await runTransaction(db, async (transaction) => {
      const actual = await transaction.get(ref);
      if (actual.exists()) {
        throw errorConCodigo(COLOCADOR_DUPLICADO, `Ya existe un colocador con el CUIT ${cuitDigits}.`);
      }
      transaction.set(ref, {
        nombre: nombreLimpio,
        cuit: cuitDigits,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: String(auth.currentUser?.email || '').toLowerCase(),
      });
    });
    return cuitDigits;
  } catch (error) {
    if (error.code !== COLOCADOR_DUPLICADO) console.error('colocadoresService.createColocador failed:', error);
    throw error;
  }
};

/** Cambia el nombre (el CUIT no se edita: es el id). */
export const renameColocador = async (id, nombre) => {
  const nombreLimpio = String(nombre || '').trim();
  if (!nombreLimpio) throw errorConCodigo('invalid-argument', 'El nombre es obligatorio.');
  try {
    await updateDoc(doc(db, COLECCION, id), { nombre: nombreLimpio, updatedAt: serverTimestamp() });
  } catch (error) {
    console.error('colocadoresService.renameColocador failed:', { id, error });
    throw error;
  }
};

/** Soft delete (active:false) o reactivación. Las licitaciones ya cargadas no cambian. */
export const setColocadorActivo = async (id, active) => {
  try {
    await updateDoc(doc(db, COLECCION, id), { active: !!active, updatedAt: serverTimestamp() });
  } catch (error) {
    console.error('colocadoresService.setColocadorActivo failed:', { id, active, error });
    throw error;
  }
};
